/**
 * Đăng ký service worker (public/sw.js) + luồng cập nhật (skipWaiting theo yêu cầu người dùng,
 * không tự động, tránh gián đoạn giữa luồng 5 bước — xem comment ở sw.js `install`).
 *
 * PWA install prompt: KHÔNG bắt trong in-app browser (docs/73-facebook-integration.md §3.4 —
 * WebView của Facebook/Messenger/Instagram không expose `beforeinstallprompt`, và kể cả nếu có
 * bắn, cài đặt từ trong WebView không hoạt động đúng cách) — component gọi `capturedPrompt` phải
 * tự kiểm tra `detectInAppBrowser().isInApp` trước khi hiển thị nút cài đặt, hàm ở đây chỉ lo
 * phần bắt sự kiện trình duyệt.
 */

export type PwaUpdateHandlers = {
  /** Gọi khi có bản service worker mới đã cài xong, đang chờ activate. */
  onUpdateAvailable?: () => void;
};

/** Đăng ký service worker. An toàn gọi ở môi trường không hỗ trợ (không throw). */
export function registerServiceWorker(handlers: PwaUpdateHandlers = {}): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      // scope thu hẹp về /tao-qr — app tạo QR giờ phục vụ ở đó, không phải "/" (Phase 8, SEO
      // landing). Trang marketing tĩnh ("/", "/huong-dan"...) không có JS nên không bao giờ gọi
      // hàm này, nhưng thu hẹp scope tường minh ở đây để service worker không lỡ can thiệp vào
      // các trang đó nếu registration cũ từng có scope rộng hơn.
      .register("/sw.js", { scope: "/tao-qr" })
      .then((registration) => {
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
              // Có SW mới cài xong VÀ đã có SW cũ đang chạy -> đây là bản cập nhật (không phải
              // lần cài đầu tiên) -> báo cho UI hiện "Có bản mới, tải lại nhé".
              handlers.onUpdateAvailable?.();
            }
          });
        });
      })
      .catch(() => {
        // Đăng ký service worker thất bại không được chặn app chạy — PWA là tăng cường
        // (progressive enhancement), không phải điều kiện để dùng được luồng 5 bước.
      });

    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  });
}

/** Gửi tín hiệu cho service worker đang chờ (waiting) activate ngay, kích hoạt reload. */
export function applyPwaUpdate(): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  navigator.serviceWorker.getRegistration().then((registration) => {
    registration?.waiting?.postMessage("SKIP_WAITING");
  });
}

export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

/**
 * Bắt sự kiện `beforeinstallprompt` (chỉ bắn ở trình duyệt độc lập đạt tiêu chí PWA — Android
 * Chrome chủ yếu; iOS Safari không bắn sự kiện này, phải hướng dẫn thủ công "Thêm vào MH chính"
 * ở UI riêng). Trả về hàm huỷ lắng nghe.
 */
export function captureInstallPrompt(onAvailable: (event: BeforeInstallPromptEvent) => void): () => void {
  const handler = (e: Event) => {
    e.preventDefault();
    onAvailable(e as BeforeInstallPromptEvent);
  };
  window.addEventListener("beforeinstallprompt", handler);
  return () => window.removeEventListener("beforeinstallprompt", handler);
}

/** true nếu app đang chạy ở chế độ standalone (đã cài đặt) — dùng để ẩn mọi lời mời cài đặt. */
export function isRunningStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const mql = window.matchMedia?.("(display-mode: standalone)");
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return Boolean(mql?.matches) || iosStandalone;
}
